import { useCallback, useRef, useState } from 'react'

import type { DisplayMode } from '../../api/boards'
import {
  type DocumentText,
  type LibraryDocument,
  changeMarkup,
  fetchText,
  isOutOfDate,
  saveText,
} from '../../api/library'
import { type Failure, asFailure } from '../../api/request'
import { useLoad } from '../../api/useLoad'
import { Button } from '../../ui/Control'
import { Preview } from '../../ui/preview/Preview'
import { Done, Failed, Waiting } from '../../ui/Status'
import { useSaving } from '../../ui/useSaving'
import { RichText } from '../../ui/RichText'

/**
 * A document that is written rather than finished, lying on a board.
 *
 * A PDF is done, and a card on one is a picture of it, a viewer for it, or slides. A marked-up text
 * is the opposite: it is on the board **because** somebody is working on it, and the walk into the
 * library and back is exactly the break a desk exists to avoid. So such a card has two live forms
 * instead of the PDF's two — the text as it is set, and the text being written — and the switch
 * between them sits on the card itself.
 *
 * Three things it deliberately does not carry, all of them the library's:
 *
 * **Showing the original** and **throwing the writing away**. Both are decisions about a document's
 * history rather than about what is on the board, and the panel beside the board opens the library
 * for them.
 *
 * **A second way to save.** This is `saveText` — the one place a document's text is written — with
 * `basedOn` and "this is the first save of a session" carried exactly as `WriteDocument` carries
 * them. One sitting at the board is therefore one version in the history, and a save that would go
 * over a newer version from somewhere else is refused rather than winning.
 */
export interface WrittenDocumentProps {
  readonly document: LibraryDocument
  /** Whether the editor is shown rather than the set text. */
  readonly writing: boolean
  /** Whether writing is offered at all: not on a phone, and not on a locked card. */
  readonly editable: boolean
  /** Persisted on the element, so the card is found in the state it was left in. */
  readonly onMode: (mode: DisplayMode) => void
}

export function WrittenDocument({ document, writing, editable, onMode }: WrittenDocumentProps) {
  const opened = useLoad(
    useCallback((signal: AbortSignal) => fetchText(document.id, undefined, signal), [document.id]),
  )

  return (
    <div
      className="thing__text"
      /*
        Reading, selecting and typing belong to the text. The caption and the card's outer edge stay
        the places that select and move it — the same division an embedded PDF already makes.
      */
      onPointerDown={(event) => {
        event.stopPropagation()
      }}
      onDoubleClick={(event) => {
        event.stopPropagation()
      }}
    >
      {opened.load.status === 'waiting' && <Waiting label="Opening the text." />}
      {opened.load.status === 'failed' && (
        <Failed
          failure={opened.load.failure}
          onRetry={opened.retry}
          title="The text did not open"
        />
      )}
      {opened.load.status === 'ready' && (
        <Text
          /*
            A different text is a different thing being written in — the same rule the library's
            editor follows. The key is deliberately *not* the document's version: the board is read
            again after every change to it, and a card that remounted on each of those would lose the
            cursor mid-sentence.
          */
          key={`${opened.load.value.versionId}-${opened.load.value.basedOn}`}
          document={document}
          opened={opened.load.value}
          writing={writing && editable}
          onRefresh={opened.retry}
        />
      )}

      <div className="thing__textControls">
        <Button
          tone="floating"
          aria-pressed={!writing}
          onClick={() => {
            onMode('document')
          }}
        >
          Read
        </Button>
        {/* Not offered where nothing is written: a phone looks at a board, a locked card is locked. */}
        {editable && (
          <Button
            tone="floating"
            aria-pressed={writing}
            onClick={() => {
              onMode('writing')
            }}
          >
            Write
          </Button>
        )}
      </div>
    </div>
  )
}

/**
 * One text that has already arrived, drawn or written in.
 *
 * Everything it holds is worked out once from that text — what is on the screen, what it is based on,
 * and whether the next save opens a version — and switching between reading and writing keeps all
 * three, because it is the same sitting either way.
 */
function Text({
  document,
  opened,
  writing,
  onRefresh,
}: {
  readonly document: LibraryDocument
  readonly opened: DocumentText
  readonly writing: boolean
  readonly onRefresh: () => void
}) {
  const [text, setText] = useState(opened.text)
  /** What the text on the screen is based on. A stale one refuses the save rather than winning. */
  const basedOn = useRef(opened.basedOn)
  /** Whether the next save opens a version. True until the first save of this sitting has gone. */
  const beginsASession = useRef(true)

  const [saved, setSaved] = useState<number | null>(null)
  const [failure, setFailure] = useState<Failure | null>(null)
  const [outOfDate, setOutOfDate] = useState(false)

  const saving = useSaving(async (wanted) => {
    if (outOfDate) {
      return
    }
    try {
      const result = await saveText(document.id, wanted, basedOn.current, beginsASession.current)
      basedOn.current = result.basedOn
      beginsASession.current = false
      setSaved(Date.now())
      setFailure(null)
    } catch (error) {
      const what = asFailure(error)
      setFailure(what)
      if (isOutOfDate(what)) {
        // Read-only from here on, and nothing is thrown away: what was typed stays on the card,
        // selectable and copyable. Refreshing is a decision somebody makes.
        setOutOfDate(true)
      }
    }
  })

  if (!writing) {
    return (
      <div className="thing__reading">
        <Preview text={text} format={opened.mediaType} label={document.title} />
      </div>
    )
  }

  return (
    <div className="thing__writingIn">
      {outOfDate && (
        <p className="thing__stale" role="alert">
          Changed elsewhere — this card is read-only now. Nothing you typed is lost.{' '}
          <Button tone="bare" onClick={onRefresh}>
            Refresh
          </Button>
        </p>
      )}
      {failure !== null && !outOfDate && <Failed failure={failure} />}

      <RichText
        compact
        text={text}
        format={opened.mediaType}
        label={document.title}
        purpose="board-document"
        initially="text"
        readOnly={outOfDate}
        placeholder="Write. It saves itself."
        onChange={(wanted) => {
          setText(wanted)
          if (!outOfDate) {
            saving.typed(wanted)
          }
        }}
        onFormat={async (wanted) => {
          // A label on the document, not a change to the text: no byte moves and no version is
          // added. Read back, because what the editor highlights by comes from the fetched text.
          await changeMarkup(document.id, wanted)
          onRefresh()
        }}
      />

      <p className="thing__state">
        {outOfDate ? <span>Read only</span> : <span>Saved as you write</span>}
        {saved !== null && !outOfDate && <Done key={saved} label="Saved" />}
      </p>
    </div>
  )
}
